import React, { useEffect, useState } from 'react';
import { describeEmbedCheck, fetchVideoInfo, formatDuration, isVideoId, thumbnailUrl, watchUrl } from './youtube';

const infoCache = new Map(); // video id -> result of fetchVideoInfo

// YouTube's own answer about a video, fetched once per video
function useVideoInfo(id) {
  const [info, setInfo] = useState(() => (id && infoCache.get(id)) || null);

  useEffect(() => {
    if (!isVideoId(id)) {
      setInfo(null);
      return undefined;
    }
    if (infoCache.has(id)) {
      setInfo(infoCache.get(id));
      return undefined;
    }
    let cancelled = false;
    setInfo(null);
    fetchVideoInfo(id).then((result) => {
      infoCache.set(id, result);
      if (!cancelled) setInfo(result);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return info;
}

function TrackDetails({ track }) {
  const current = useVideoInfo(track?.id);
  const next = useVideoInfo(track?.nextId);

  if (!track || !track.id) {
    return (
      <div className="fm-track-slot">
        <p className="fm-track-empty">track details will appear once the playlist loads</p>
      </div>
    );
  }

  const title = track.title || current?.title || '';
  const author = track.author || current?.author || '';
  const length = formatDuration(track.duration);

  return (
    <div className="fm-track-slot">
      <div className="fm-track" aria-label="Track details">
        <img className="fm-track-thumb" src={thumbnailUrl(track.id)} alt="" width="160" height="90" loading="lazy" referrerPolicy="no-referrer" />
        <dl className="fm-track-facts">
          <dt>Title</dt>
          <dd className="fm-track-title">{title || "(YouTube hasn't shared it)"}</dd>
          {author && (<><dt>Channel</dt><dd>{author}</dd></>)}
          {track.index !== null && track.total && (
            <><dt>Track</dt><dd>{track.index + 1} of {track.total} (shuffled order)</dd></>
          )}
          {length && (<><dt>Length</dt><dd>{length}</dd></>)}
          <dt>Video ID</dt>
          <dd>
            <code>{track.id}</code>{' '}
            <a className="ms-link" href={watchUrl(track.id)} target="_blank" rel="noopener noreferrer">watch on YouTube ↗</a>
          </dd>
          <dt>Embed check</dt>
          <dd>{describeEmbedCheck(current)}</dd>
        </dl>

        {track.nextId && (
          <p className="fm-track-next">
            <strong>Up next:</strong>{' '}
            {next?.title || <code>{track.nextId}</code>}
            {next?.author ? ` · ${next.author}` : ''}{' '}
            <a className="ms-link" href={watchUrl(track.nextId)} target="_blank" rel="noopener noreferrer">watch ↗</a>
          </p>
        )}
      </div>
    </div>
  );
}

export default TrackDetails;
